import React, { FC, useRef, useState, useEffect } from 'react';
import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

import {
  Box,
  Flex,
  Text,
  Stack,
  Switch,
  Button,
  Divider,
  Skeleton,
  IconButton,
  MenuItem,
  Menu,
  MenuButton,
  MenuList,
} from '@chakra-ui/react';
import {
  MdEdit,
  MdChevronLeft,
  MdChevronRight,
  MdCalendarToday,
} from 'react-icons/md';

import { format, isToday } from 'date-fns';

import { calculateWeek, findWeekOffset } from 'utils';

import { useQuery } from 'react-query';
import { fetchOrders } from 'api/order';
import { fetchPickups } from 'api/pickup';

import { useSKU, useSites } from 'hooks';

import { selectors } from 'store/slices/washhub';
import { useCurrentWeek } from 'store/slices/currentWeek';

import { scrollbarStyles } from 'theme';

import { AnimatePresence, motion } from 'framer-motion';

import { EditOrderModal, EditOrderModalRef } from '../EditOrderModal';
import { EditPickupModal, EditPickupModalRef } from '../EditPickupModal';

import { Order } from 'types/order';
import { Pickup } from 'types/pickup';
import { Status } from 'types/items';
import { EventsCalendarProps } from './EventsCalendar.props';
import { startOfDay, isEqual } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { convertUTCToLocal, toApiPayloadDateFormat } from 'utils/dateUtils';
import { fetchAllCounts } from 'api/inventory';
import { InventoryCount, InventoryCountStatus } from 'types/inventory';
import EditInventoryCountModal, {
  EditRefType,
} from '../EditInventoryCountModal';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { downloadPickups } from 'components/pdfs/MultiplePickupDetailsPdfTemplate';
import { downloadOrders } from 'components/pdfs/MultipleOrderDetailsTemplate';

// Convert date to the start of the day in UTC timezone
const toUTCStartOfDay = (date: Date) => {
  const utcDate = utcToZonedTime(date, 'UTC');
  return startOfDay(utcDate);
};

type Detail = Order['details'][0];

const MotionFlex = motion(Flex);

const getMotionProps = (index: number) => ({
  initial: {
    opacity: 0,
    scale: 0.5,
  },
  animate: {
    opacity: 1,
    scale: 1,
  },
  transition: {
    delay: 0.03 * index,
    ease: 'easeInOut',
    duration: 0.3,
  },
  exit: {
    opacity: 0,
    scale: 0.5,
  },
});

const DEFAULT_DATA = {
  orders: [],
  pickups: [],
  counts: [],
};

const useOrdersAndPickupsAndCountsQuery = (
  startDate: Date,
  endDate: Date,
  isOnlyOpen: boolean,
  onSuccess: () => void,
) => {
  const currentWashhubId = selectors.useSelectedWashHubId();

  const queryKey = [
    `fetch-orders`,
    `fetch-pickups`,
    `fetch-inventory-counts`,
    startDate,
    endDate,
    currentWashhubId,
    isOnlyOpen,
  ];
  const queryFn = async () => {
    const ordersResponse = await fetchOrders({
      startDate,
      endDate,
      washHub: currentWashhubId,
      limit: 100,
      ...(isOnlyOpen ? { status: Status.OPEN } : {}),
    });
    const pickupsResponse = await fetchPickups({
      startDate,
      endDate,
      washHub: currentWashhubId,
      limit: 100,
      ...(isOnlyOpen ? { status: Status.OPEN } : {}),
    });
    const countsResponse = await fetchAllCounts({
      limit: 100,
      washHub: currentWashhubId,
      endDate,
      startDate,
      ...(isOnlyOpen
        ? { status: InventoryCountStatus.SCHEDULED }
        : {
            statuses: [
              InventoryCountStatus.COUNTS_ENTERED,
              InventoryCountStatus.DONE,
              InventoryCountStatus.PENDING_PICKUP,
              InventoryCountStatus.READY_FOR_REPORT,
            ],
            archived: false,
          }),
    });

    return {
      orders: ordersResponse.items,
      pickups: pickupsResponse.items,
      counts: countsResponse.items,
    };
  };

  const query = useQuery(queryKey, queryFn, { onSuccess });

  return query;
};

const EventsCalendarView: FC<EventsCalendarProps> = () => {
  const [urlRecord, setUrlRecord] = useState({});
  const selectedWashHub = selectors.useSelectedWashHub();
  const washHubName = selectedWashHub?.name;
  const washHubId = selectedWashHub?._id;
  const isWashHubSelected = washHubId !== '';

  const phcMethod = selectedWashHub?.phcMethod ?? 0;
  // eslint-disable-next-line eqeqeq
  const isRacked = phcMethod == 0;

  const queryUrl = useQueryParams();

  useUrlSharing(urlRecord, washHubId);

  const { data: skus = [] } = useSKU(
    'package',
    queryUrl.washHubId || washHubId,
  );
  const { data: sites } = useSites(true);

  const skusMap = skus.reduce((acc, cur) => {
    acc[cur._id] = cur?.name || '';

    return acc;
  }, {} as Record<string, string>);

  const editOrderModalRef = useRef<EditOrderModalRef>(null);
  const editPickupModalRef = useRef<EditPickupModalRef>(null);
  const editInventoryCountModalRef = useRef<EditRefType>(null);

  const [currentWeek, setCurrentWeek] = useCurrentWeek();

  const offsetRef = useRef(
    +(queryUrl.weekoffset || findWeekOffset(currentWeek)),
  );

  const [isOnlyOpen, setIsOnlyOpen] = useState<boolean>(true);

  const [selectedEvent, setSelectedEvent] = useState<
    Pickup | Order | InventoryCount | null
  >(null);

  const weekStartDate = currentWeek[0];
  const weekEndDate = currentWeek[currentWeek.length - 1];

  const weekStart = format(weekStartDate, 'M/dd');
  const weekEnd = format(weekEndDate, 'M/dd');

  const { data = DEFAULT_DATA, isFetching } = useOrdersAndPickupsAndCountsQuery(
    weekStartDate,
    weekEndDate,
    isOnlyOpen,
    () => {
      setSelectedEvent(null);
    },
  );

  useEffect(() => {
    const urlParams = {
      washHubId: String(washHubId),
      weekoffset: String(offsetRef.current),
      onlyopen: String(isOnlyOpen),
    };
    setUrlRecord(urlParams);
  }, [currentWeek, isOnlyOpen, washHubId]);

  useEffect(() => {
    const { onlyopen, weekoffset } = queryUrl;

    if (weekoffset) {
      setCurrentWeek(calculateWeek(offsetRef.current));
    }
    if (onlyopen) {
      setIsOnlyOpen(!!(onlyopen === 'true'));
    }
  }, []);

  const renderEventCard = () => {
    if (!selectedEvent) {
      return null;
    }

    const isOrder = 'orderNumber' in selectedEvent;
    const isPickup = 'pickupNumber' in selectedEvent;
    const isCount = 'countNumber' in selectedEvent;

    if (isOrder) {
      const order = selectedEvent;

      const sortDetails = (a: Detail, b: Detail) => {
        const skuA = String(skusMap[a?.sku] || '').toLowerCase();
        const skuB = String(skusMap[b?.sku] || '').toLowerCase();

        return skuA.localeCompare(skuB);
      };

      return (
        <>
          <Flex align="center" justify="space-between">
            <Flex gap="10px" align="center">
              <MdCalendarToday size="18px" />
              <Text fontWeight="600">Order Details</Text>
            </Flex>

            <IconButton
              size="xs"
              variant="outline"
              aria-label="edit-order"
              icon={<MdEdit size="14px" />}
              onClick={() => editOrderModalRef.current?.onOpen?.(order)}
            />
          </Flex>

          <AnimatePresence exitBeforeEnter>
            <MotionFlex
              bg="#fff"
              key={order._id}
              shadow="lg"
              p="10px"
              pr="20px"
              pb="20px"
              borderRadius="10px"
              direction="column"
              {...getMotionProps(0)}>
              <Flex gap="10px" align="center">
                <Box h="15px" w="15px" borderRadius="15px" bg="#00BF13" />
                <Text noOfLines={1}>{order.site?.name || ''}</Text>
              </Flex>
              <Text opacity="0.6" ml="25px" fontSize="14px">
                Order No: {order.orderNumber || '-'}
              </Text>
              <Divider my="5px" borderColor="#ccc" />

              <Stack mt="10px" ml="25px">
                <Text>
                  Delivery Date:{' '}
                  {convertUTCToLocal(order?.deliveryAt, true, true)}
                </Text>
                <Text>
                  Address:{' '}
                  {order.site?.address?.fullAddress ||
                    order.site?.address?.addressLine1 ||
                    '-'}
                </Text>
                <Text>Notes: {order.notes || '-'}</Text>
                <Text textDecoration="underline">Skus</Text>
                <Flex
                  opacity={0.6}
                  flex={1}
                  fontSize="14px"
                  borderBottom="1px solid #ccc">
                  <Text flex={1.2}>Name</Text>
                  <Text flex={1} textAlign="center">
                    Cases Count
                  </Text>
                </Flex>
                <Stack
                  fontSize="14px"
                  spacing={1}
                  maxH="100px"
                  overflowY="auto"
                  css={scrollbarStyles}>
                  {order.details?.sort(sortDetails)?.map((detail) => {
                    const sku = skus.find((s) => detail.sku === s._id);

                    return (
                      <Flex
                        key={detail._id}
                        flex={1}
                        borderBottom="1px solid #ccc">
                        <Text flex={1.2} isTruncated>
                          {sku?.name || detail._id}
                        </Text>
                        <Text flex={1} textAlign="center">
                          {detail.casesCount}
                        </Text>
                      </Flex>
                    );
                  })}
                </Stack>
              </Stack>
            </MotionFlex>
          </AnimatePresence>

          <EditOrderModal
            ref={editOrderModalRef}
            renderNextOrderButton={() => null}
            renderPreviousOrderButton={() => null}
          />
        </>
      );
    } else if (isPickup) {
      const pickup = selectedEvent;
      const site = pickup.collectionPoint as any;

      return (
        <>
          <Flex align="center" justify="space-between">
            <Flex gap="10px" align="center">
              <MdCalendarToday size="18px" />
              <Text fontWeight="600">Pickup Details</Text>
            </Flex>

            <IconButton
              size="xs"
              variant="outline"
              aria-label="edit-order"
              icon={<MdEdit size="14px" />}
              onClick={() => editPickupModalRef.current?.onOpen?.(pickup)}
            />
          </Flex>
          <AnimatePresence exitBeforeEnter>
            <MotionFlex
              bg="#fff"
              shadow="lg"
              p="10px"
              pr="20px"
              pb="20px"
              borderRadius="10px"
              direction="column"
              key={pickup._id}
              {...getMotionProps(0)}>
              <Flex gap="10px" align="center">
                <Box h="15px" w="15px" borderRadius="15px" bg="#00BF13" />
                <Text noOfLines={1}>{pickup.collectionPoint.name || ''}</Text>
              </Flex>
              <Divider my="5px" borderColor="#ccc" />
              <Stack mt="10px" ml="25px">
                <Text>Pickup No: {pickup.pickupNumber || '-'}</Text>
                <Text>
                  Pickup Date: {convertUTCToLocal(pickup?.date, true, true)}
                </Text>
                <Text>Event Name: {pickup?.eventName || '-'}</Text>
                <Text>
                  Address:{' '}
                  {site?.address?.fullAddress ||
                    site?.address?.addressLine1 ||
                    '-'}
                </Text>
                <Text>Notes: {pickup.notes || '-'}</Text>
              </Stack>
            </MotionFlex>
          </AnimatePresence>

          <EditPickupModal
            ref={editPickupModalRef}
            renderNextPickupButton={() => null}
            renderPreviousPickupButton={() => null}
          />
        </>
      );
    } else if (isCount) {
      const count = selectedEvent;
      const site = sites?.find((_site) => _site._id === count?.site);
      return (
        <>
          <Flex align="center" justify="space-between">
            <Flex gap="10px" align="center" justify="space-between">
              <MdCalendarToday size="18px" />
              <Text fontWeight="600">Count Details</Text>
            </Flex>
            <IconButton
              size="xs"
              variant="outline"
              aria-label="edit-order"
              icon={<MdEdit size="14px" />}
              onClick={() =>
                editInventoryCountModalRef.current?.onOpen?.(count)
              }
            />
          </Flex>
          <AnimatePresence exitBeforeEnter>
            <MotionFlex
              bg="#fff"
              shadow="lg"
              p="10px"
              pr="20px"
              pb="20px"
              borderRadius="10px"
              direction="column"
              key={count._id}
              {...getMotionProps(0)}>
              <Flex gap="10px" align="center">
                <Box h="15px" w="15px" borderRadius="15px" bg="#00BF13" />
                <Text noOfLines={1}>{site?.name || ''}</Text>
              </Flex>
              <Divider my="5px" borderColor="#ccc" />
              <Stack mt="10px" ml="25px">
                <Text>Count No: {count.countNumber || '-'}</Text>
                <Text>
                  Count Date: {convertUTCToLocal(count?.countDate, true, true)}
                </Text>
                <Text>
                  Address:{' '}
                  {site?.address?.fullAddress ||
                    site?.address?.addressLine1 ||
                    '-'}
                </Text>
                <Text>Notes: {count.notes || '-'}</Text>
              </Stack>
            </MotionFlex>
          </AnimatePresence>
          <EditInventoryCountModal ref={editInventoryCountModalRef} />
        </>
      );
    }
  };

  return (
    <Flex
      w="100%"
      flexWrap={{ base: 'wrap', lg: 'nowrap' }}
      gap={{ base: '20px', lg: '0' }}>
      <Flex
        minH="550px"
        maxH="550px"
        minW="600px"
        bg="#fff"
        flex={1}
        overflow="hidden"
        zIndex={1}
        direction="column"
        borderRadius="10px"
        boxShadow="0 0 30px 0 rgba(0,0,0,0.1)">
        <Flex
          position="relative"
          px="20px"
          pt="20px"
          align="center"
          justify="space-between"
          gap="10px">
          <Text
            color="primary.300"
            fontSize={{ base: '16px', lg: '24px' }}
            fontWeight="600">
            Calendar
          </Text>
          <Flex align="center" gap="20px">
            <IconButton
              size="sm"
              aria-label="left-icon"
              icon={<MdChevronLeft size="30px" />}
              variant="ghost"
              onClick={() => {
                offsetRef.current--;

                setCurrentWeek(calculateWeek(offsetRef.current));
              }}
            />

            <Text fontSize={{ base: '12px', lg: '18px' }} fontWeight="500">
              This week {weekStart} - {weekEnd}
            </Text>

            <IconButton
              size="sm"
              aria-label="right-icon"
              icon={<MdChevronRight size="30px" />}
              variant="ghost"
              onClick={() => {
                offsetRef.current++;

                setCurrentWeek(calculateWeek(offsetRef.current));
              }}
            />
          </Flex>

          <Button
            size="sm"
            w="fit-content"
            onClick={() => {
              offsetRef.current = 0;
              setCurrentWeek(calculateWeek());
            }}>
            Today
          </Button>
        </Flex>

        <Flex align="center" gap="10px" pl="20px">
          <Text>Show Only Open</Text>
          <Switch
            size="sm"
            colorScheme="primary"
            isChecked={isOnlyOpen}
            onChange={(e) => setIsOnlyOpen(e.target.checked)}
          />
        </Flex>

        <Flex mt="15px" flex={1} gap="1px">
          <Flex
            w="100px"
            h="100%"
            direction="column"
            align="center"
            borderRightWidth="2px">
            <Box h="80px" w="100%" />

            <Box h="25px" p="5px" />

            <Flex my="20px" flex={1} align="center" fontWeight="600">
              Pickups
            </Flex>
            <Divider borderWidth="1px" />
            <Flex my="20px" flex={1} align="center" fontWeight="600">
              Orders
            </Flex>
            <Divider borderWidth="1px" />
            <Flex my="20px" flex={1} align="center" fontWeight="600">
              Counts
            </Flex>
          </Flex>
          <AnimatePresence exitBeforeEnter>
            {currentWeek.map((day, index) => {
              const weekDay = format(day, 'eee');
              const dayFormat = format(day, 'dd');

              const currentOrders = data.orders.filter((order) => {
                const orderDate = toUTCStartOfDay(
                  convertUTCToLocal(order.deliveryAt) as Date,
                );
                const dayDate = toUTCStartOfDay(convertUTCToLocal(day) as Date);

                return isEqual(orderDate, dayDate);
              });

              const currentPickups = data.pickups.filter((pickup) => {
                const pickupDate = toUTCStartOfDay(
                  convertUTCToLocal(pickup.date) as Date,
                );
                const dayDate = toUTCStartOfDay(convertUTCToLocal(day) as Date);
                return isEqual(pickupDate, dayDate);
              });

              const currentCounts = data.counts.filter((count) => {
                const countDate = toUTCStartOfDay(
                  convertUTCToLocal(count.countDate) as Date,
                );
                const dayDate = toUTCStartOfDay(convertUTCToLocal(day) as Date);

                return isEqual(countDate, dayDate);
              });

              const totalEvents =
                currentOrders.length +
                currentPickups.length +
                currentCounts.length;

              return (
                <MotionFlex
                  p="10px 3px"
                  flex={1}
                  maxW="150px"
                  {...getMotionProps(index)}
                  direction="column"
                  bg={isToday(day) ? '#60B480' : ''}
                  fontWeight={isToday(day) ? 'bold' : '500'}
                  color={isToday(day) ? '#fff' : ''}
                  align="center"
                  borderTopRadius="10px"
                  key={dayFormat}>
                  <Box
                    h="65px"
                    w="100%"
                    borderBottomWidth="2px"
                    position={'relative'}>
                    <Text textAlign="center" fontSize="20px">
                      {weekDay}
                    </Text>
                    <Text textAlign="center">{dayFormat}</Text>
                    {isWashHubSelected && (
                      <div
                        style={{
                          width: '100%',
                          position: 'absolute',
                          top: '7px',
                          right: '4px',
                          height: '100%',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'start',
                        }}>
                        <Menu>
                          <MenuButton
                            as={Box}
                            icon={<BsThreeDotsVertical />}
                            size="xs"
                            top="0px"
                            right="0px"
                            cursor="pointer">
                            <BsThreeDotsVertical />
                          </MenuButton>
                          <MenuList>
                            <MenuItem
                              style={{
                                color: 'primary.400',
                              }}
                              onClick={() =>
                                downloadPickups(currentPickups, {
                                  fileName: `${washHubName}-Pickups-${toApiPayloadDateFormat(
                                    day,
                                  )}.pdf`,
                                  isRacked,
                                })
                              }>
                              Download Pickups
                            </MenuItem>
                            <MenuItem
                              style={{
                                color: 'primary.400',
                              }}
                              onClick={() => {
                                console.log('currentOrders', currentOrders);
                                downloadOrders(currentOrders, {
                                  fileName: `${washHubName}-Deliveries-${toApiPayloadDateFormat(
                                    day,
                                  )}.pdf`,
                                });
                              }}>
                              Download Deliveries
                            </MenuItem>
                            <MenuItem
                              style={{
                                color: 'primary.400',
                              }}
                              onClick={() => {
                                downloadPickups(currentPickups, {
                                  fileName: `${washHubName}-Pickups-${toApiPayloadDateFormat(
                                    day,
                                  )}.pdf`,
                                  isRacked,
                                });
                                downloadOrders(currentOrders, {
                                  fileName: `${washHubName}-Deliveries-${toApiPayloadDateFormat(
                                    day,
                                  )}.pdf`,
                                });
                              }}>
                              Download All
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </div>
                    )}
                  </Box>
                  <Box
                    mt="5px"
                    p="5px"
                    w="100%"
                    borderBottomRadius="10px"
                    textAlign="center"
                    fontWeight="600"
                    noOfLines={1}
                    color={isToday(day) ? '#fff' : '#B29620'}
                    bg={isToday(day) ? '#DFC86A' : '#F2E9D7'}>
                    {totalEvents} events
                  </Box>

                  <Flex
                    my="10px"
                    flex={1}
                    w="100%"
                    py="1px"
                    maxH="150px"
                    h="100%"
                    overflow="auto"
                    justify="center"
                    css={scrollbarStyles}>
                    {isFetching ? (
                      <Stack w="100%">
                        <Skeleton height="20px" w="100%" colorScheme="green" />
                        <Skeleton height="20px" w="100%" />
                        <Skeleton height="20px" w="100%" />
                      </Stack>
                    ) : (
                      <Stack>
                        {currentPickups.map((pickup) => (
                          <Box
                            as="button"
                            type="button"
                            key={pickup._id + index}
                            bg="surface.400"
                            borderRadius="10px"
                            p="5px"
                            color="support.500"
                            fontWeight="600"
                            fontSize="12px"
                            cursor="pointer"
                            userSelect="none"
                            border={`2px solid ${
                              selectedEvent?._id === pickup?._id
                                ? '#FF7000'
                                : 'transparent'
                            }`}
                            onClick={() => setSelectedEvent(pickup)}>
                            <Text
                              textAlign="center"
                              w="fit-content"
                              noOfLines={1}>
                              {(pickup.collectionPoint as any)?.name ||
                                pickup._id}
                            </Text>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Flex>
                  <Divider
                    borderWidth="1px"
                    borderColor={isToday(day) ? '#fff' : '#e1e1e1'}
                  />
                  <Flex
                    py="1px"
                    my="10px"
                    flex={1}
                    maxH="150px"
                    w="100%"
                    justify="center"
                    overflow="auto"
                    css={scrollbarStyles}>
                    {isFetching ? (
                      <Stack w="100%">
                        <Skeleton height="20px" w="100%" colorScheme="green" />
                        <Skeleton height="20px" w="100%" />
                        <Skeleton height="20px" w="100%" />
                      </Stack>
                    ) : (
                      <Stack>
                        {currentOrders.map((order) => (
                          <Box
                            as="button"
                            type="button"
                            textAlign="center"
                            key={order._id + index}
                            bg="surface.400"
                            color="support.500"
                            fontWeight="600"
                            fontSize="12px"
                            border={`2px solid ${
                              selectedEvent?._id === order?._id
                                ? '#FF7000'
                                : 'transparent'
                            }`}
                            borderRadius="10px"
                            p="5px"
                            cursor="pointer"
                            userSelect="none"
                            onClick={() => setSelectedEvent(order)}>
                            <Text
                              alignSelf="center"
                              textAlign="center"
                              w="fit-content"
                              noOfLines={1}>
                              {order?.site?.name || order._id}
                            </Text>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Flex>
                  <Divider
                    borderWidth="1px"
                    borderColor={isToday(day) ? '#fff' : '#e1e1e1'}
                  />
                  <Flex
                    py="1px"
                    my="10px"
                    flex={1}
                    maxH="150px"
                    w="100%"
                    justify="center"
                    overflow="auto"
                    css={scrollbarStyles}>
                    {isFetching ? (
                      <Stack w="100%">
                        <Skeleton height="20px" w="100%" colorScheme="green" />
                        <Skeleton height="20px" w="100%" />
                        <Skeleton height="20px" w="100%" />
                      </Stack>
                    ) : (
                      <Stack>
                        {currentCounts.map((count) => {
                          const site = sites?.find(
                            (_site) => _site._id === count?.site,
                          );

                          return (
                            <Box
                              as="button"
                              type="button"
                              textAlign="center"
                              key={count._id + index}
                              bg="surface.400"
                              color="support.500"
                              fontWeight="600"
                              fontSize="12px"
                              border={`2px solid ${
                                selectedEvent?._id === count?._id
                                  ? '#FF7000'
                                  : 'transparent'
                              }`}
                              borderRadius="10px"
                              p="5px"
                              cursor="pointer"
                              userSelect="none"
                              onClick={() => setSelectedEvent(count)}>
                              <Text
                                alignSelf="center"
                                textAlign="center"
                                w="fit-content"
                                noOfLines={1}>
                                {site?.name || count._id}
                              </Text>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Flex>
                </MotionFlex>
              );
            })}
          </AnimatePresence>
        </Flex>
      </Flex>

      {/* <PDFViewer> */}
      {/* <MultiplePickupDetailsPdfTemplate pickups={data.pickups} /> */}
      {/* </PDFViewer> */}

      <Flex
        maxH="550px"
        overflowY="auto"
        direction="column"
        ml={{ base: '0', lg: '-10px' }}
        p="20px"
        pl="30px"
        gap="10px"
        maxW="400px"
        w="100%"
        flex={1}
        bg="#C5EED180">
        {renderEventCard()}
      </Flex>
    </Flex>
  );
};

export default EventsCalendarView;
